import * as React from 'react';

import type {FilterObject} from 'nutshell-core/types';

import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {Illustration} from 'shells/illustrations/illustration';

import type {CampaignType} from 'nutshell-graphql-types';

import {getReportDefaultFields} from '../../../reports/report-constants';
import type {ReportURLParams} from '../../../reports/report-types';

import {EntityListContainer} from '../../entity-lists/entity-list-container';

import {EditionRecipientListProvider} from './edition-recipient-list-provider';
import {adjustFiltersForComparisonEdition} from './helpers';

type Props = {
    editionId: string;
    comparisonEditionId: string | null | undefined;
    campaignType: CampaignType;
    unfilteredCount: number;
    filters: FilterObject[];
    search: string;
    selectedPage: number;
    selectedSortField: string;
    selectedFields: string[];
    urlParams: ReportURLParams<'contacts'>;
    onParamChange: (arg1: any, arg2: boolean) => any;
    onRemoveAllFilters: () => void;
    graduatedFilters: React.ReactNode;
    fieldFilterFunction: (arg1: string) => boolean;
};

export function EditionRecipientListContainer(props: Props) {
    const {comparisonEditionId, graduatedFilters, fieldFilterFunction, ...restProps} = props;
    const campaignTypeNoun = getCampaignTypeNoun(props.campaignType);

    const adjustedFilters: FilterObject[] = comparisonEditionId
        ? adjustFiltersForComparisonEdition(
              props.filters,
              comparisonEditionId,
              props.urlParams.version
          )
        : props.filters;

    return (
        <EditionRecipientListProvider>
            <EntityListContainer
                {...restProps}
                filters={adjustedFilters}
                entityType='contacts'
                customEmptyStateComponent={
                    <EmptyStateSmall
                        title={`It looks like this ${campaignTypeNoun} didn’t have any recipients`}
                        subtitle={`When you send a ${campaignTypeNoun} to one or more recipients, they’ll show up here`}
                        icon={
                            <div style={{width: '75px'}}>
                                <Illustration
                                    src='magnifying-glass-empty-state'
                                    width='100%'
                                    height='100%'
                                />
                            </div>
                        }
                    />
                }
                graduatedFilters={graduatedFilters}
                fieldFilterFunction={fieldFilterFunction}
                unfilteredCount={props.unfilteredCount}
                unfilteredCountThreshold={0}
                parentEntityId={props.editionId}
                scrollContainerSelector='#edition-recipient-list'
                defaultFields={getReportDefaultFields('editionEngagement').split(',')}
                hasPeopleListFooter={true}
            />
        </EditionRecipientListProvider>
    );
}

// @ts-expect-error
const getCampaignTypeNoun = (campaignType) => {
    let noun = 'broadcast';

    switch (campaignType) {
        case 'NEWSLETTER':
            noun = 'edition';
            break;
        case 'DRIP_SEQUENCE':
            noun = 'message';
            break;
    }

    return noun;
};
